import React from "react";
import { AppConfigContext } from "contexts/app/appConfig";
import { fetchZekay } from "api/zekay/httpClient";

export interface SSEEvent {
  entity: string;
  action: string;
  entity_id: number;
  actor_id: number;
}

type SSECallback = (event: SSEEvent) => void;

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

/**
 * Opens a long-lived SSE connection to GET /sse using the patched fetch
 * (which injects X-Company-ID). Parses "data:" lines and calls `onEvent`.
 * Auto-reconnects with exponential back-off on disconnect.
 */
export function useSSE(onEvent: SSECallback) {
  const appConfig = React.useContext(AppConfigContext);
  const callbackRef = React.useRef(onEvent);
  callbackRef.current = onEvent;

  const abortRef = React.useRef<AbortController | null>(null);

  const connect = React.useCallback(
    async (baseURL: string, signal: AbortSignal, attempt: number) => {
      try {
        const res = await fetchZekay(`${baseURL}/sse`, {
          signal,
          headers: { Accept: "text/event-stream" },
        });

        if (!res.ok || !res.body) return;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done || signal.aborted) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const evt = JSON.parse(line.slice(6)) as SSEEvent;
              callbackRef.current(evt);
            } catch {
              // malformed JSON — skip
            }
          }
        }
      } catch {
        if (signal.aborted) return;
      }

      if (signal.aborted) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (!signal.aborted) connect(baseURL, signal, attempt + 1);
    },
    [],
  );

  React.useEffect(() => {
    if (!appConfig?.zekayAPI) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    connect(appConfig.zekayAPI, ctrl.signal, 0);

    return () => {
      ctrl.abort();
      abortRef.current = null;
    };
  }, [appConfig, connect]);
}
